// File: frontend/src/utils/venues.ts
//
// Partner bars that run their own weekend contest (BAR-CONTESTS-SCOPE-2026-10-06.md).
// Each lives at fantasy.futbol/<slug> (unlisted) and brands only the brand
// surfaces: background, cards, the gold accent, the heading/body fonts and a
// logo next to "FANTASY FUTBOL". Meaningful colors (injury, positions, status,
// ranks) never change. Names, prize text and entry codes come from the
// backend (backend/app/core/venues.py) - keep the slugs in step.
//
// Import-free so `node --test` can load it directly.

/** "r g b", the format the Tailwind tokens read (rgb(var(--x) / <alpha-value>)). */
type Rgb = string;

export interface VenueTheme {
  slug: string;
  name: string;
  logo: string;
  /** Logo height in the header, px (the Dubliner's badge is tall, the Banshee's wordmark wide). */
  logoHeight: number;
  /** Set the bar's name as text in the header instead of its logo. */
  headerText?: string;
  /** How that text is set, to match the bar's own wordmark (defaults to the heading font). */
  headerTextStyle?: { fontFamily?: string; letterSpacing?: string };
  colors: {
    primary: Rgb;
    primaryDark: Rgb;
    surfaceCard: Rgb;
    surfaceRaised: Rgb;
    accent: Rgb;
    /** Secondary labels and links: the app's light blue, re-colored per bar. */
    alt: Rgb;
  };
  fonts: { heading: string; body: string };
  googleFontsHref: string;
}

/** Today's Fantasy Futbol values; index.css sets the same on :root. */
export const DEFAULT_BRAND_VARIABLES: Record<string, string> = {
  '--ff-primary': '16 36 68',
  '--ff-primary-dark': '12 28 54',
  '--ff-surface-card': '21 47 90',
  '--ff-surface-raised': '26 59 107',
  '--ff-accent': '255 196 44',
  '--ff-alt': '160 196 236',
  '--ff-accent-border': '234 179 8', // Tailwind yellow-500, the gold controls' edge
  '--ff-surface-input': '17 24 39', // Tailwind gray-900, text fields
  '--ff-font-heading': "'Titillium Web'",
  '--ff-font-body': "'Roboto'",
};

export const VENUE_THEMES: Record<string, VenueTheme> = {
  // thedublinerboston.com: black and gold, Playfair Display headings.
  dubliner: {
    slug: 'dubliner',
    name: 'The Dubliner',
    logo: '/venues/dubliner/logo.png',
    logoHeight: 34,
    // The badge is unreadable at header size (owner 2026-10-06).
    headerText: 'The Dubliner',
    colors: {
      primary: '20 18 15',
      primaryDark: '10 9 8',
      surfaceCard: '34 31 26',
      surfaceRaised: '48 43 35',
      accent: '226 173 55',
      alt: '236 208 147',
    },
    fonts: { heading: "'Playfair Display'", body: "'Open Sans'" },
    googleFontsHref: 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=Open+Sans:wght@400;500;600;700&display=swap',
  },
  // bansheeboston.com: black, orange and deep red, Bebas Neue + Jost.
  banshee: {
    slug: 'banshee',
    name: 'The Banshee',
    logo: '/venues/banshee/logo.png',
    logoHeight: 22,
    colors: {
      primary: '17 17 17',
      primaryDark: '6 6 6',
      surfaceCard: '34 24 22',
      surfaceRaised: '52 33 30',
      accent: '255 166 70',
      alt: '255 214 170',
    },
    fonts: { heading: "'Bebas Neue'", body: "'Jost'" },
    googleFontsHref: 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Jost:wght@400;500;600;700&display=swap',
  },
  // longcrossbar.com (Medford): black and white, with the badge's sage cream as
  // the one accent; Heebo headings, Montserrat for the wordmark and body.
  longcross: {
    slug: 'longcross',
    name: 'LongCross',
    logo: '/venues/longcross/logo.png',
    logoHeight: 34,
    // The badge's black lettering vanishes on the dark header, so the name is
    // set like their site title: uppercase Montserrat, tracked 3px.
    headerText: 'LONGCROSS',
    headerTextStyle: { fontFamily: "'Montserrat'", letterSpacing: '3px' },
    colors: {
      primary: '12 12 12',
      primaryDark: '0 0 0',
      surfaceCard: '27 27 27',
      surfaceRaised: '40 40 40',
      accent: '198 201 172',
      alt: '235 235 235',
    },
    fonts: { heading: "'Heebo'", body: "'Montserrat'" },
    googleFontsHref: 'https://fonts.googleapis.com/css2?family=Heebo:wght@700;800&family=Montserrat:wght@400;500;600;700&display=swap',
  },
};

export const VENUE_SLUGS: string[] = Object.keys(VENUE_THEMES);

export const venueTheme = (slug: string | null | undefined): VenueTheme | null =>
  (slug && VENUE_THEMES[slug.trim().toLowerCase()]) || null;

/** The CSS variables a bar overrides; the defaults when no bar is active. */
/** A darker shade of a color, for the edge of accent-filled controls. */
const shade = (rgb: Rgb, factor = 0.8): Rgb =>
  rgb.split(' ').map(channel => Math.round(Number(channel) * factor)).join(' ');

export function brandVariables(theme: VenueTheme | null): Record<string, string> {
  if (!theme) return { ...DEFAULT_BRAND_VARIABLES };
  return {
    '--ff-primary': theme.colors.primary,
    '--ff-primary-dark': theme.colors.primaryDark,
    '--ff-surface-card': theme.colors.surfaceCard,
    '--ff-surface-raised': theme.colors.surfaceRaised,
    '--ff-accent': theme.colors.accent,
    '--ff-alt': theme.colors.alt,
    '--ff-accent-border': shade(theme.colors.accent),
    '--ff-surface-input': theme.colors.primaryDark,
    '--ff-font-heading': theme.fonts.heading,
    '--ff-font-body': theme.fonts.body,
  };
}

/** Where a bar's entry code is remembered on this device once accepted. */
export const venueCodeStorageKey = (slug: string): string => `ff:venueCode:${slug}`;

/** The bar to brand: the most recently registered one still mounted. */
export function activeVenueSlug(registrations: { slug: string | null }[]): string | null {
  for (let i = registrations.length - 1; i >= 0; i--) {
    const theme = venueTheme(registrations[i].slug);
    if (theme) return theme.slug;
  }
  return null;
}

/** The bar's entry code this device already got right, or undefined. */
export function readVenueCode(slug: string | null | undefined): string | undefined {
  if (!slug) return undefined;
  try {
    return globalThis.localStorage?.getItem(venueCodeStorageKey(slug)) ?? undefined;
  } catch {
    return undefined;
  }
}

export function saveVenueCode(slug: string, code: string): void {
  try {
    globalThis.localStorage?.setItem(venueCodeStorageKey(slug), code);
  } catch {
    /* private mode: the code is asked for again next visit */
  }
}

export function forgetVenueCode(slug: string): void {
  try {
    globalThis.localStorage?.removeItem(venueCodeStorageKey(slug));
  } catch {
    /* nothing stored */
  }
}
